import { DOCUMENT } from '@angular/common';
import { inject } from '@angular/core';
import { CanMatchFn, Router } from '@angular/router';
import { catchError, map, of } from 'rxjs';

import { AuthService } from './auth.service';

function isMockMode(): boolean {
  const doc = inject(DOCUMENT);
  const w = doc.defaultView;
  const search = w?.location?.search ?? '';
  const params = new URLSearchParams(search);
  return (params.get('data') ?? '').toLowerCase() === 'mock';
}

function getShortLivedTokenFromUrl(): string {
  const doc = inject(DOCUMENT);
  const w = doc.defaultView;
  const search = w?.location?.search ?? '';
  const params = new URLSearchParams(search);
  return (params.get('short_lived_token') ?? '').trim();
}

export const authGuard: CanMatchFn = (_route, segments) => {
  if (isMockMode()) return true;

  const auth = inject(AuthService);
  const router = inject(Router);

  // If a short-lived token is present on a protected route (e.g. auth redirected to /app),
  // redirect to landing while preserving it so the landing page can exchange it.
  // Don't add ?redirect= here — the landing page extracts the intended post-login
  // path from the original navigation URL via the Performance API.
  const token = getShortLivedTokenFromUrl();
  if (token) {
    return router.parseUrl(`/?short_lived_token=${encodeURIComponent(token)}`);
  }

  if (auth.isAuthenticated()) return true;

  // Build a landing-page URL that includes the intended path as ?redirect=,
  // so after login the user is sent back to where they were trying to go.
  const intendedPath = '/' + segments.map((s) => s.path).join('/');
  const landing = router.parseUrl('/');
  if (intendedPath !== '/' && intendedPath !== '/app') {
    landing.queryParams = { redirect: intendedPath };
  }

  // For HttpOnly-cookie auth, we can't read tokens. Use the backend session probe to decide.
  return auth.probeSession().pipe(
    map((ok) => (ok ? true : landing)),
    catchError(() => of(landing)),
  );
};
